/**
 * Phase 2.5: Structured Note Preview (Bottom Collapsible Panel)
 * 
 * Real-time preview of operative note structure.
 * Surgeon does not need to interact unless desired.
 */

import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { StructuredOpNote } from '@/lib/dictation/types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface StructuredNotePreviewProps {
  note: StructuredOpNote;
}

const SECTION_CONFIG: Array<{
  key: keyof Omit<StructuredOpNote, 'fullText'>;
  label: string;
}> = [
  { key: 'indications', label: 'Indications' },
  { key: 'anesthesia', label: 'Anesthesia' },
  { key: 'findings', label: 'Findings' },
  { key: 'procedures', label: 'Procedures' },
  { key: 'implants', label: 'Implants' },
  { key: 'closure', label: 'Closure' },
  { key: 'estimatedBloodLoss', label: 'Est. Blood Loss' },
  { key: 'specimens', label: 'Specimens' },
  { key: 'complications', label: 'Complications' },
];

export function StructuredNotePreview({ note }: StructuredNotePreviewProps) {
  const [isOpen, setIsOpen] = useState(false);

  const populatedCount = SECTION_CONFIG.filter(s => note[s.key]?.trim()).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className={cn(
          'w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors',
          'bg-card/50 border-border/50 hover:border-primary/30'
        )}>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Structured Preview</span>
            <span className="text-xs text-muted-foreground">
              {populatedCount}/{SECTION_CONFIG.length} sections
            </span>
          </div>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SECTION_CONFIG.map(({ key, label }) => {
            const content = note[key];
            const hasContent = !!content?.trim();
            return (
              <div
                key={key}
                className={cn(
                  'px-3 py-2 rounded-lg border text-xs',
                  hasContent
                    ? 'bg-card border-border/50'
                    : 'bg-muted/20 border-border/20'
                )}
              >
                <p className={cn(
                  'font-semibold uppercase tracking-wider mb-1',
                  hasContent ? 'text-primary/80' : 'text-muted-foreground/40'
                )}>
                  {label}
                </p>
                {hasContent ? (
                  <p className="text-foreground/80 line-clamp-3 whitespace-pre-wrap">
                    {content}
                  </p>
                ) : (
                  <p className="text-muted-foreground/30 italic">Awaiting dictation…</p>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
